import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { subscription } from '@/lib/api';
import { getStoredUser } from '@/lib/auth';

// Only employers have an account-level billing subscription; a Sub (TOC) has
// no plan of their own — GET /subscription/status 403s for that role. TOC
// screens still need the plan catalog (to look up a *school's* plan by id
// when gating messaging), just not the account usage/limits.
const PlanLimitsContext = createContext(null);

export function PlanLimitsProvider({ children }) {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Employer-only: re-fetch the account usage counts (toc_count, schools_count,
  // postings_this_month) that drive the proactive disabled states. Called after
  // any mutation that changes usage (approve/archive/unarchive Sub, create/
  // archive/unarchive school, post position) so `isAtLimit` re-computes without
  // a page reload. The plan catalog is NOT re-fetched — it is static per session.
  const refreshStatus = async () => {
    const user = getStoredUser();
    if (user?.role !== 'employer') return;
    try {
      const statusResponse = await subscription.getAccountStatus();
      setStatus(statusResponse.data || null);
    } catch (_e) {
      // Keep the previous snapshot on failure — a stale count is better than
      // wiping limits to null (which would flash every control enabled).
    }
  };

  useEffect(() => {
    let isMounted = true;
    const user = getStoredUser();

    async function load() {
      try {
        const plansRequest = subscription.getPlans(undefined, true);
        const statusRequest = user?.role === 'employer'
          ? subscription.getAccountStatus()
          : Promise.resolve(null);
        const [plansResponse, statusResponse] = await Promise.all([plansRequest, statusRequest]);
        if (!isMounted) return;
        setPlans(plansResponse.data || []);
        setStatus(statusResponse?.data || null);
      } catch (error) {
        if (isMounted) {
          setPlans([]);
          setStatus(null);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    load();
    return () => { isMounted = false; };
  }, []);

  // Internal (non-purchasable) plans such as trial_premium are kept in plansById so limit/feature
  // lookups resolve, but excluded from `plans`, which pickers and comparisons iterate.
  const purchasablePlans = useMemo(() => plans.filter((planEntry) => !planEntry.is_internal), [plans]);

  const plansById = useMemo(() => {
    const map = {};
    for (const planEntry of plans) {
      map[planEntry.plan_id] = planEntry;
    }
    return map;
  }, [plans]);

  const value = useMemo(() => ({
    plan: status?.plan ?? null,
    features: status?.features ?? plansById[status?.plan]?.features ?? [],
    limits: {
      schools: status?.school_limit ?? null,
      subs: status?.toc_limit ?? null,
      postings: status?.postings_limit ?? null,
      coAdmins: status?.co_admins_limit ?? null,
    },
    counts: {
      schools: status?.schools_count ?? null,
      subs: status?.toc_count ?? null,
      postings: status?.postings_this_month ?? null,
      coAdmins: status?.co_admins_count ?? null,
    },
    // Trial Premium state (status === 'trial_premium'); daysRemaining is null off-trial.
    trial: {
      isActive: status?.status === 'trial_premium',
      endsAt: status?.trial_ends_at ?? null,
      daysRemaining: status?.trial_days_remaining ?? null,
      used: Boolean(status?.trial_used),
    },
    plans: purchasablePlans,
    plansById,
    isLoading,
    refreshStatus,
  }), [status, plans, purchasablePlans, plansById, isLoading]);

  return (
    <PlanLimitsContext.Provider value={value}>
      {children}
    </PlanLimitsContext.Provider>
  );
}

export function usePlanLimitsContext() {
  const ctx = useContext(PlanLimitsContext);
  if (!ctx) {
    throw new Error('usePlanLimitsContext must be used within a PlanLimitsProvider');
  }
  return ctx;
}
